<template>
  <CustomDrawer
    :title="tt('tenant.drawer.header')"
    class="add-tenant-drawer"
    visible
    @close="onClose"
  >
    <Form
      ref="formRef"
      :layout="ELayout.vertical"
      :model="modelRef"
      :rules="rulesRef"
      autocomplete="off"
    >
      <Divider
        class="add-tenant-drawer__main-info-divider"
        orientation="left"
      >
        {{ tt('shared.mainInfoTitle') }}
      </Divider>
      <p class="add-tenant-drawer__subtitle">
        {{ tt('tenant.shared.mainInfoSubtitle') }}
      </p>
      <FormItem
        :label="tt('shared.companyName')"
        v-bind="validateInfos.name"
        name="name"
      >
        <ContractorsSelect
          placeholder=""
          :items="options"
          :isLoading="isOptionsLoading"
          :value="modelRef.name"
          @select="handleTenantChange"
          @search="handleSearch"
        />
      </FormItem>
      <div class="add-tenant-drawer__row">
        <FormItem
          class="add-tenant-drawer__row-item add-tenant-drawer__row-item_left"
          :label="tt('shared.inn')"
          v-bind="validateInfos.inn"
          name="inn"
        >
          <Input
            :value="modelRef.inn"
            isDigital
            :isInvalid="isFieldInvalid('inn')"
            @change="handleInputChange('inn', $event.target.value)"
          />
        </FormItem>
        <FormItem
          class="add-tenant-drawer__row-item"
          :label="tt('shared.kpp')"
          v-bind="validateInfos.kpp"
          name="kpp"
        >
          <Input
            :value="modelRef.kpp"
            isDigital
            :isInvalid="isFieldInvalid('kpp')"
            @change="handleInputChange('kpp', $event.target.value)"
          />
        </FormItem>
      </div>
      <FormItem
        class="add-tenant-drawer__row-item_left add-tenant-drawer__row-item_half"
        v-bind="validateInfos.phone"
        :label="tt('shared.phone')"
        name="phone"
      >
        <PhoneInput
          :value="modelRef.phone"
          placeholder=""
          :hasMask="false"
          @input="handleInputChange('phone', $event.target.value)"
        />
      </FormItem>
      <div class="add-tenant-drawer__row">
        <FormItem
          name="legalAddress"
          class="add-tenant-drawer__row-item"
          v-bind="validateInfos.legalAddress"
          :label="tt('shared.legalAddress')"
        >
          <Input
            :value="modelRef.legalAddress"
            @change="handleInputChange('legalAddress', $event.target.value)"
          />
        </FormItem>
      </div>
      <div class="add-tenant-drawer__vat-rate-block">
        <FormItem
          class="add-tenant-drawer__item"
          :label="tt('tenant.shared.form.taxType')"
          v-bind="validateInfos.taxType"
          name="taxType"
        >
          <RadioGroup
            v-model:value="modelRef.taxType"
            :optionType="ERadioButtonOptionType.button"
            :options="taxType"
          />
        </FormItem>

        <FormItem
          v-if="isVatRatesV2Available"
          class="add-tenant-drawer__vat-rate-select"
          name="vatRateSlug"
          v-bind="validateInfos.vatRateSlug"
          :label="`${tt('shared.vat')}, %`"
        >
          <Select
            :value="modelRef.vatRateSlug"
            :options="availableVatRatesOptions"
            :isInvalid="isFieldInvalid('vatRateSlug')"
            labelInValue
            showArrow
            @change="handleVatRateChange"
          />
        </FormItem>
      </div>
      <Divider orientation="left">
        {{ tt('tenant.shared.bankDetailsTitle') }}
      </Divider>
      <p class="add-tenant-drawer__subtitle">
        {{ tt('tenant.shared.bankDetailsSubtitle') }}
      </p>
      <div class="add-tenant-drawer__row">
        <FormItem
          class="add-tenant-drawer__row-item add-tenant-drawer__row-item_left"
          :label="tt('tenant.shared.form.bankBik')"
          v-bind="validateInfos.bankBik"
          name="bankBik"
        >
          <BankSelector
            :items="bankOptions"
            :isLoading="isOptionsBankLoading"
            :value="modelRef.bankBik"
            dropdownClassName="add-tenant-drawer__bank-dropdown"
            @change="handleBankChange"
            @search="handleSearchBankChange"
          />
        </FormItem>
        <FormItem
          class="add-tenant-drawer__row-item"
          :label="tt('tenant.shared.form.bankCheckingAccount')"
          v-bind="validateInfos.bankCheckingAccount"
          name="bankCheckingAccount"
        >
          <Input
            :value="modelRef.bankCheckingAccount"
            isDigital
            :isInvalid="isFieldInvalid('bankCheckingAccount')"
            @change="handleInputChange('bankCheckingAccount', $event.target.value)"
          />
        </FormItem>
      </div>
      <BankAccountInfo
        v-if="showBankAccount"
        :bank="bankInfo"
      />
    </Form>
    <template #footer>
      <Button
        class="add-tenant-drawer__close-btn"
        :title="tt('shared.action.cancel')"
        @click.prevent="onClose"
      />
      <Button
        :type="EButtonType.primary"
        :disabled="submitDisabled"
        :title="tt('tenant.drawer.register')"
        @click.prevent="onSubmit"
      />
    </template>
  </CustomDrawer>
</template>

<script lang="ts">
import {
  Divider,
  Form,
  FormItem,
} from 'ant-design-vue';
import debounce from 'lodash.debounce';
import {
  computed,
  defineComponent,
  onMounted,
  toRefs,
} from 'vue';

import {
  Input,
  PhoneInput,
  RadioGroup,
  Button,
  Select,
} from '@/ui';
import ContractorsSelect from '@/components/ContractorsSelect/index.vue';
import { CustomDrawer } from '@/ui/experimental';
import tt from '@/i18n/utils/translateText';
import { DEFAULT_DEBOUNCE_DELAY, ELayout } from '@/constants';
import { ECompanyRole, EExperimentalFeatures } from '@/domains/constants';
import {
  ERadioButtonOptionType,
  EButtonType,
} from '@/ui/types';
import { checkFeatureIsEnabled } from '@/domains/checkFeatureIsEnabled';

import BankAccountInfo from '../BankAccountInfo/index.vue';
import BankSelector from '../BankSelector/index.vue';
import { useInteractWithForm } from './application/useInteractWithForm';

export default defineComponent({
  name: 'AddTenantDrawer',
  components: {
    Divider,
    CustomDrawer,
    Button,
    Form,
    FormItem,
    RadioGroup,
    ContractorsSelect,
    BankSelector,
    BankAccountInfo,
    Input,
    PhoneInput,
    Select,
  },
  props: {
    formDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'success'],
  setup(props, { emit }) {
    const isVatRatesV2Available = computed(() => checkFeatureIsEnabled(EExperimentalFeatures.vatRatesV2));
    const { formDisabled } = toRefs(props);
    const {
      validateInfos,
      rulesRef,
      modelRef,
      isOptionsLoading,
      isOptionsBankLoading,
      options,
      bankOptions,
      formRef,
      showBankAccount,
      bankInfo,
      submitDisabled,
      isLoading,
      taxType,
      availableVatRatesOptions,

      isFieldInvalid,
      setFormData,
      handleBankChange,
      handleTenantChange,
      handleInputChange,
      handleSearchBankChange,
      handleVatRateChange,
      handleSearch,
      onSubmit,
      onClose,
    } = useInteractWithForm({
      isVatRatesV2Available,
      formDisabled,
      emit,
    });

    onMounted(setFormData);

    return {
      modelRef,
      formRef,
      rulesRef,
      bankInfo,
      ELayout,
      ECompanyRole,
      ERadioButtonOptionType,
      options,
      bankOptions,
      isOptionsLoading,
      isOptionsBankLoading,
      showBankAccount,
      isLoading,
      submitDisabled,
      taxType,
      EButtonType,
      isVatRatesV2Available,
      availableVatRatesOptions,
      validateInfos,

      tt,
      onSubmit,
      onClose,
      handleSearch: debounce(handleSearch, DEFAULT_DEBOUNCE_DELAY),
      handleSearchBankChange: debounce(handleSearchBankChange, DEFAULT_DEBOUNCE_DELAY),
      handleTenantChange,
      handleBankChange,
      handleInputChange,
      handleVatRateChange,
      isFieldInvalid,
    };
  },
});
</script>

<style lang="scss" src="./styles.scss" />
